<template>
  <div class="bg p-3 mb-auto h-full overflow-auto bg-gradient-to-b from-gray-900 to-black" id="style-1">
    <div v-if="isLoading" class="flex justify-center items-center h-64">
      <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
      <p class="text-indigo-200 text-sm ml-4">กำลังโหลดข้อมูลวิญญาณ...</p>
    </div>
    <div v-else-if="error" class="text-center text-red-500 mt-4">
      {{ error }}
    </div>
    <div v-else-if="ghosts.length === 0" class="text-center text-indigo-200 mt-4">
      คุณยังไม่มีวิญญาณในโถ
    </div>
    <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <div
        v-for="ghost in ghosts"
        :key="ghost.id"
        class="bg-gray-900 bg-opacity-80 rounded-lg p-3 border border-indigo-900 transition-all duration-300 hover:shadow-xl hover:border-indigo-500 relative cursor-pointer"
      >
        <div v-if="equippedGhostId === ghost.id" class="absolute top-1 right-1 bg-green-600 text-white text-xs px-2 py-1 rounded-full shadow-md">
          กำลังใช้งาน
        </div>
        <div class="flex items-center mb-2">
          <img
            :src="ghost.img || 'default-ghost.png'"
            alt="Ghost Image"
            :class="[ghost.rarityColors.borderColor]"
            class="w-12 h-12 rounded-full object-cover border-2 mr-3 shadow-md"
            @error="handleImageError"
          />
          <div class="flex flex-col w-full">
            <div class="flex items-center space-x-1">
              <div class="flex items-center space-x-1">
                <div class="p-0.5 text-xs flex justify-center items-center  bg-gray-900 border-gray-700  rounded text-white border ">
                  <i class="fas fa-star text-yellow-500 mr-1" ></i> 
                  <div class="text-white font-bold">{{ ghost.rarity }}</div> 
                </div>
                <div :class="ghost.rarityColors.textColor" class="text-white font-bold">{{ ghost.name }}</div>
              </div>
            </div>
            <!-- HP Bar -->
            <div class="flex flex-col space-y-1 w-full mb-2">
              <div class="flex justify-between items-center text-xs">
                <div class="flex items-center space-x-1">
                  <i class="fa-solid fa-heart text-red-500"></i>
                  <span class="font-bold" :class="{ 'text-red-500': ghost.hpPercentage <= 20, 'text-white': ghost.hpPercentage > 20 }">{{ ghost.currentHp }}/{{ ghost.calculatedStats.maxHp }}</span>
                </div>
                <div class="text-gray-400">พลังชีวิต</div>
              </div>
              <div class="w-full bg-gray-800 rounded-full h-1.5">
                <div class="bg-red-600 h-1.5 rounded-full transition-all duration-300" :style="{ width: `${ghost.hpPercentage}%` }"></div>
              </div>
            </div>
          </div>
        </div>
        <!-- Power Stats - Compact Grid -->
        <div class="grid grid-cols-2 gap-2 text-xs mb-3">
          <div class="flex justify-between bg-gray-800 p-2 rounded">
            <span class="text-indigo-300 flex items-center">
              <i class="fas fa-heartbeat text-indigo-500 mr-1"></i> เลือด
            </span>
            <span class="font-semibold text-white">{{ ghost.calculatedStats.maxHp }}</span>
          </div>
          <div class="flex justify-between bg-gray-800 p-2 rounded">
            <span class="text-indigo-300 flex items-center">
              <i class="fas fa-fist-raised text-indigo-500 mr-1"></i> โจมตี
            </span>
            <span class="font-semibold text-white">{{ ghost.calculatedStats.atk }}</span>
          </div>
          <div class="flex justify-between bg-gray-800 p-2 rounded">
            <span class="text-indigo-300 flex items-center">
              <i class="fas fa-crosshairs text-indigo-500 mr-1"></i> คริติคอล
            </span>
            <span class="font-semibold text-white">{{ ghost.calculatedStats.critical }}%</span>
          </div>
          <div class="flex justify-between bg-gray-800 p-2 rounded">
            <span class="text-indigo-300 flex items-center">
              <i class="fas fa-wind text-indigo-500 mr-1"></i> หลบหลีก
            </span>
            <span class="font-semibold text-white">{{ ghost.calculatedStats.evasion }}%</span>
          </div>
          <div class="flex justify-between bg-gray-800 p-2 rounded ">
            <span class="text-indigo-300 flex items-center">
              <i class="fas fa-shield-alt text-indigo-500 mr-1"></i> ป้องกัน
            </span>
            <span class="font-semibold text-white">{{ ghost.calculatedStats.defense }}</span>
          </div>
        </div>
        <!-- Buttons - Unified Indigo Theme with Gradients and Icons -->
        <div class="flex justify-between space-x-2">
          <button
            @click="openEquipModal(ghost.id)"
            class="flex-1 px-3 py-3 bg-gradient-to-r from-indigo-600 to-indigo-800 text-white rounded-md text-xs font-semibold hover:from-indigo-500 hover:to-indigo-700 transition-all duration-200 shadow-md flex items-center justify-center"
          >
            <i class="fas fa-ghost mr-1"></i> ใช้งาน
          </button>
          <button
            @click="openEvolveModal(ghost.id)"
            class="flex-1 px-3 py-3 bg-gradient-to-r from-indigo-600 to-indigo-800 text-white rounded-md text-xs font-semibold hover:from-indigo-500 hover:to-indigo-700 transition-all duration-200 shadow-md flex items-center justify-center"
          >
            <i class="fas fa-hammer mr-1"></i> พัฒนา
          </button>
          <button
            @click="openDecomposeModal(ghost.id)"
            class="flex-1 px-3 py-3 bg-gradient-to-r from-indigo-600 to-indigo-800 text-white rounded-md text-xs font-semibold hover:from-indigo-500 hover:to-indigo-700 transition-all duration-200 shadow-md flex items-center justify-center"
          >
            <i class="fas fa-tools mr-1"></i> ย่อยสลาย
          </button>
        </div>
      </div>
    </div>
    <!-- Modals removed -->
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import { projectFirestore } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';

export default {
  components: {
  },
  setup() {
    const { user } = getUser();
    const ghosts = ref([]);
    const isLoading = ref(true);
    const error = ref(null);
    const equippedGhostId = ref(null);

    const playerStore = usePlayerStore();

    watch(() => playerStore.characterPrivateData, (data) => {
      if (data) {
        equippedGhostId.value = data.equippedGhostId || null;
      } else {
        equippedGhostId.value = null;
      }
    }, { immediate: true });

    const fetchGhostJars = async () => {
      try {
        isLoading.value = true;
        error.value = null;
        if (!user.value) {
          error.value = 'กรุณาล็อกอินเพื่อดูข้อมูล';
          return;
        }
        const uid = user.value.uid;
        const ghostJarsDoc = await projectFirestore.collection('ghostJars').doc(uid).get();
        if (!ghostJarsDoc.exists) {
          ghosts.value = [];
          return;
        }
        const ghostIds = ghostJarsDoc.data().ghostIds || [];
        if (ghostIds.length === 0) {
          ghosts.value = [];
          return;
        }
        // Fetch each ghost document
        const ghostPromises = ghostIds.map(id => projectFirestore.collection('ghosts').doc(id).get());
        const ghostDocs = await Promise.all(ghostPromises);
        ghosts.value = ghostDocs
          .filter(doc => doc.exists)
          .map(doc => {
            const data = doc.data();
            const stats = data.stats || {};
            const calculatedStats = {
              maxHp: (stats.stamina || 0) * 20 + (stats.maxHp || 0),
              atk: (stats.str || 0) * 2 + (stats.atk || 0),
              critical: Math.floor((stats.int || 0) / 2) + (stats.critical || 0),
              evasion: Math.floor((stats.agi || 0) / 2) + (stats.flee || 0),
              defense: stats.defense || 0
            };
            const damageTaken = data.damageTaken || 0;
            const currentHp = Math.max(0, calculatedStats.maxHp - damageTaken);
            const hpPercentage = calculatedStats.maxHp > 0 ? Math.min((currentHp / calculatedStats.maxHp) * 100, 100) : 0;
            return {
              id: doc.id,
              ...data,
              currentForm: data.currentForm || 1,
              calculatedStats,
              damageTaken,
              currentHp,
              hpPercentage
            };
          });
      } catch (err) {
        console.error('Error fetching ghost jars:', err);
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูล';
      } finally {
        isLoading.value = false;
      }
    };

    const handleImageError = (event) => {
      event.target.src = 'default-ghost.png';
    };

    onMounted(() => {
      fetchGhostJars();
    });

    return {
      ghosts,
      isLoading,
      error,
      equippedGhostId,
      handleImageError,
      openEquipModal,
      openEvolveModal,
      openDecomposeModal,
      fetchGhostJars
    };
  }
};
</script>

<style scoped>
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fmanagerroom.png?alt=media&token=6b9348b6-9364-4ce5-bf58-6e7b9c5b12f1') no-repeat center center fixed,
              linear-gradient(to bottom, rgba(31, 31, 55, 0.9), rgba(0, 0, 0, 1));
  background-size: cover;
}

/* Custom Scrollbar */
#style-1::-webkit-scrollbar {
  width: 6px;
}
#style-1::-webkit-scrollbar-track {
  background: rgba(31, 41, 55, 0.8);
  border-radius: 10px;
}
#style-1::-webkit-scrollbar-thumb {
  background: #6366f1; /* Indigo theme */
  border-radius: 10px;
}
#style-1::-webkit-scrollbar-thumb:hover {
  background: #4f46e5;
}
</style>